import { useEffect } from 'react';

import type { MapRef } from 'react-map-gl/maplibre';

/**
 * Ensures a MapLibre map resizes correctly when:
 * - The map becomes visible again after being hidden (`active` flips true).
 * - The container element changes size (e.g. responsive layout).
 *
 * @param map - The MapRef from MapLibre.
 * @param active - Whether the map is currently visible (non-zero size).
 * @param containerRef - Ref to the map container element.
 */
const useMapSizing = (
  map: MapRef | undefined | null,
  active: boolean,
  containerRef: React.RefObject<HTMLElement | null>,
): void => {
  useEffect(() => {
    if (!map || !active) {
      return () => null;
    }

    const id = requestAnimationFrame(() => map.resize());

    return () => cancelAnimationFrame(id);
  }, [map, active]);

  useEffect(() => {
    if (!map || !containerRef.current) {
      return () => null;
    }

    const observer = new ResizeObserver(() => map.resize());
    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, [map, containerRef]);
};

export { useMapSizing };
